/**
 * Assign a punch to an attendance calendar day using shift + policy interval rules.
 *
 * Attendance timezone is always Asia/Kolkata.
 * Browser/device timezone must never affect attendance-day calculations.
 */

const ATTENDANCE_TIMEZONE = "Asia/Kolkata";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function istPartsFromMs(ms) {
  const value = Number(ms);
  if (!Number.isFinite(value)) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: ATTENDANCE_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);

  const result = {};
  for (const part of parts) {
    if (part.type !== "literal") {
      result[part.type] = part.value;
    }
  }

  return {
    year: Number(result.year),
    month: Number(result.month),
    day: Number(result.day),
    hour: Number(result.hour),
    minute: Number(result.minute),
  };
}

export function localDayKeyFromMs(ms) {
  const parts = istPartsFromMs(ms);
  if (!parts) return null;

  return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;
}

export function parseTimeToMinutesSafe(value) {
  if (value == null || value === "") return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const parts = istPartsFromMs(value.getTime());
    return parts ? parts.hour * 60 + parts.minute : null;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    const parts = istPartsFromMs(value);
    return parts ? parts.hour * 60 + parts.minute : null;
  }

  const s = String(value).trim();
  const m = s.match(/(\d{1,2}):(\d{2})/);

  if (!m) return null;

  return Number(m[1]) * 60 + Number(m[2]);
}

export function shiftWindowFromShift(shift) {
  const start = parseTimeToMinutesSafe(shift?.startTime) ?? 9 * 60;
  const end = parseTimeToMinutesSafe(shift?.endTime) ?? 18 * 60;

  const overnight = !!shift?.crossDayShift || end <= start;

  let durationMin = overnight ? 24 * 60 - start + end : end - start;

  if (!Number.isFinite(durationMin) || durationMin <= 0) {
    durationMin = 9 * 60;
  }

  return { start, end, overnight, durationHours: durationMin / 60 };
}

function addDaysYmd(ymd, delta) {
  const [y, m, d] = String(ymd).split("-").map(Number);

  if (
    !Number.isFinite(y) ||
    !Number.isFinite(m) ||
    !Number.isFinite(d)
  ) {
    return null;
  }

  // Use UTC only as a calendar arithmetic helper.
  // The resulting date is still interpreted as an IST calendar date.
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + delta);

  return `${dt.getUTCFullYear()}-${pad2(
    dt.getUTCMonth() + 1
  )}-${pad2(dt.getUTCDate())}`;
}

export function getPunchDayPolicy(policy = {}) {
  const mode = ["auto", "shift_window", "calendar_date"].includes(policy.punchDayMode)
    ? policy.punchDayMode
    : "auto";

  const minHoursBetweenShifts = Math.max(
    0,
    Number(policy.minHoursBetweenShifts ?? 8)
  );

  const dayEndCheckHours = Math.max(0, Number(policy.dayEndCheckHours ?? 8));

  const overnightOutAttachesToInDay = policy.overnightOutAttachesToInDay !== false;

  return {
    mode,
    minHoursBetweenShifts,
    dayEndCheckHours,
    overnightOutAttachesToInDay,
  };
}

/**
 * @param {object} params
 * @returns {{
 *   attendanceDate: string,
 *   kind: 'in'|'out',
 *   reason: string,
 *   newDay: boolean
 * }}
 */
export function assignPunchToAttendanceDay({
  punchMs,
  punchKind = "auto",
  previousClosedOutMs = null,
  openSessionInMs = null,
  openSessionDate = null,
  shift = null,
  policy = {},
} = {}) {
  const punch = Number(punchMs);

  if (!Number.isFinite(punch)) {
    return {
      attendanceDate: null,
      kind: "in",
      reason: "invalid_punch",
      newDay: false,
    };
  }

  const calendarDate = localDayKeyFromMs(punch);

  if (!calendarDate) {
    return {
      attendanceDate: null,
      kind: "in",
      reason: "invalid_punch",
      newDay: false,
    };
  }

  const cfg = getPunchDayPolicy(policy);
  const window = shiftWindowFromShift(shift);
  const punchParts = istPartsFromMs(punch);

  if (!punchParts) {
    return { attendanceDate: null, kind: "in", reason: "invalid_punch", newDay: false,};
  }

  const punchMin =
    punchParts.hour * 60 + punchParts.minute;

  if (cfg.mode === "calendar_date") {
    const kind = punchKind === "out" ? "out" : "in";

    return { attendanceDate: calendarDate, kind, reason: "calendar_date", newDay: true,};
  }

  if (openSessionInMs && punchKind !== "in") {
    const inDate = openSessionDate || localDayKeyFromMs(openSessionInMs);

    const hoursSinceIn = (punch - Number(openSessionInMs)) / 3600000;

    const maxSession = (window.durationHours || 12) + cfg.dayEndCheckHours + 4;

    if (
      cfg.overnightOutAttachesToInDay &&
      hoursSinceIn >= 0 &&
      hoursSinceIn <= Math.max(maxSession, 20)
    ) {
      return {
        attendanceDate: inDate,
        kind: "out",
        reason: window.overnight ? "overnight_out_same_shift_day" : "open_session_out",
        newDay: false,
      };
    }
  }

  const inNextShiftWindow = window.overnight
    ? punchMin >= window.start ||
      punchMin <= window.end
    : punchMin >= window.start &&
      punchMin <= window.end;

  // OUT must not inherit lastClosed's day — fall through to overnight (overnight
  // shifts only) or explicit calendar-day OUT.
  if (previousClosedOutMs && punchKind !== "out") {
    const gapHours = (punch - Number(previousClosedOutMs)) / 3600000;

    const prevDay = localDayKeyFromMs(previousClosedOutMs);

    if (gapHours >= cfg.minHoursBetweenShifts) {
      return {
        attendanceDate: calendarDate,
        kind: "in",
        reason: "inter_shift_rest_elapsed",
        newDay: calendarDate !== prevDay,
      };
    }

    if (inNextShiftWindow && gapHours > 0) {
      return {
        attendanceDate: calendarDate,
        kind: "in",
        reason: "next_shift_window",
        newDay: calendarDate !== prevDay,
      };
    }

    return {
      attendanceDate: calendarDate,
      kind: "in",
      reason: "within_inter_shift_gap_same_day",
      newDay: false,
    };
  }

  // overnight_morning_out only when shift is overnight (crossDay / end <= start).
  if ((cfg.mode === "shift_window" || cfg.mode === "auto") && window.overnight) {
    if (punchMin < window.end && punchKind !== "in") {
      return {
        attendanceDate: addDaysYmd(calendarDate, -1),
        kind: "out",
        reason: "overnight_morning_out",
        newDay: false,
      };
    }

    if (punchMin < window.end && !openSessionInMs && punchKind === "in") {
      return {
        attendanceDate: calendarDate,
        kind: "in",
        reason: "overnight_morning_new_in",
        newDay: true,
      };
    }
  }

  if (punchKind === "out") {
    return {
      attendanceDate: calendarDate,
      kind: "out",
      reason: "explicit_out",
      newDay: false,
    };
  }

  if (punchKind === "in") {
    return {
      attendanceDate: calendarDate,
      kind: "in",
      reason: "explicit_in",
      newDay: true,
    };
  }

  const kind = openSessionInMs ? "out" : "in";

  return {
    attendanceDate: calendarDate,
    kind,
    reason: "default_calendar",
    newDay: kind === "in",
  };
}

export function isPastDayEndCheck({ nowMs, shiftEndMs, policy = {},}) {
  const hours = getPunchDayPolicy(policy).dayEndCheckHours;

  if (!shiftEndMs || !nowMs) return false;

  return (nowMs - Number(shiftEndMs) >= hours * 3600000);
}
